import { useEffect, useMemo, useState } from "react"
import { Alert, Button, Modal, Radio, message } from "antd"
import { HomeOutlined } from "@ant-design/icons"
import { gen, IClub, swimminglyApi } from "./utils"
import { IClubSeasonInformation } from "./MeetActionsModal"

interface IUpdateMeetHomeClubTraditionalProps {
  visible: boolean
  setVisible: (newVisible: boolean) => void
  meetId: number
  homeClubId: number
  meetClubSeasons: IClubSeasonInformation[]
  refresh: () => void
}

const getClubLabel = (club: IClub) => club.nameLong || club.name || "Unnamed club"

export default function UpdateMeetHomeClubTraditional({
  visible,
  setVisible,
  meetId,
  homeClubId,
  meetClubSeasons,
  refresh,
}: IUpdateMeetHomeClubTraditionalProps): JSX.Element {
  const [saving, setSaving] = useState(false)
  const [selectedHomeClubId, setSelectedHomeClubId] = useState(homeClubId)

  useEffect(() => {
    if (!visible) return
    setSelectedHomeClubId(homeClubId)
  }, [homeClubId, visible])

  const participatingClubs = useMemo(() => {
    return [...meetClubSeasons]
      .map((clubSeason) => clubSeason.club)
      .filter((club): club is IClub => Boolean(club?.clubId))
      .sort((left, right) => {
        if (left.clubId === homeClubId) return -1
        if (right.clubId === homeClubId) return 1
        return getClubLabel(left).localeCompare(getClubLabel(right))
      })
  }, [homeClubId, meetClubSeasons])

  const currentHomeClub = participatingClubs.find(
    (club) => club.clubId === homeClubId,
  )

  const closeModal = () => {
    if (saving) return
    setSelectedHomeClubId(homeClubId)
    setVisible(false)
  }

  const saveHostClub = () => {
    if (!selectedHomeClubId || selectedHomeClubId === homeClubId) {
      closeModal()
      return
    }

    setSaving(true)
    swimminglyApi
      .post(gen("/api/updateMeetHomeClub"))
      .body({ meetId, homeClubId: selectedHomeClubId })
      .then((data: { status?: string; message?: string }) => {
        if (data.status !== "success") {
          throw new Error(data.message || "Problem updating host club.")
        }

        message.success(data.message || "Meet host club updated successfully.")
        refresh()
        setVisible(false)
      })
      .catch((err) => {
        console.error(err)
        message.error("Problem updating host club.")
      })
      .finally(() => setSaving(false))
  }

  return (
    <Modal
      title="Update Host Team"
      open={visible}
      onCancel={closeModal}
      footer={[
        <Button key="cancel" onClick={closeModal} disabled={saving}>
          Cancel
        </Button>,
        <Button
          key="save"
          type="primary"
          onClick={saveHostClub}
          loading={saving}
          disabled={!selectedHomeClubId || selectedHomeClubId === homeClubId}
        >
          Save
        </Button>,
      ]}
    >
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        title="Choose a new host team from the clubs already participating in this meet."
      />

      {currentHomeClub ? (
        <div
          style={{
            marginBottom: 18,
            padding: "14px 16px",
            borderRadius: 10,
            background: "linear-gradient(135deg, #f6ffed 0%, #fffbe6 100%)",
            border: "1px solid #d9f7be",
          }}
        >
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              marginBottom: 4,
              fontSize: 12,
              fontWeight: 700,
              color: "#237804",
              textTransform: "uppercase",
              letterSpacing: 0.5,
            }}
          >
            <HomeOutlined />
            Current Host Team
          </div>
          <div style={{ fontSize: 16, fontWeight: 600, color: "#101242" }}>
            {getClubLabel(currentHomeClub)}
          </div>
        </div>
      ) : null}

      <Radio.Group
        value={selectedHomeClubId}
        onChange={(event) => setSelectedHomeClubId(event.target.value)}
        style={{ width: "100%" }}
      >
        <div style={{ display: "grid", gap: 12 }}>
          {participatingClubs.map((club) => {
            const isCurrentHost = club.clubId === homeClubId
            const isSelected = club.clubId === selectedHomeClubId

            return (
              <label
                key={club.clubId}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 12,
                  padding: "12px 14px",
                  borderRadius: 10,
                  border: isSelected
                    ? "1px solid #1677ff"
                    : "1px solid #f0f0f0",
                  background: isSelected ? "#f0f7ff" : "#ffffff",
                  boxShadow: isSelected
                    ? "0 0 0 2px rgba(22, 119, 255, 0.08)"
                    : "none",
                  cursor: "pointer",
                }}
              >
                <Radio value={club.clubId}>{getClubLabel(club)}</Radio>
                {isCurrentHost ? (
                  <span style={{ color: "#8c8c8c", fontSize: 12 }}>
                    Current host
                  </span>
                ) : null}
              </label>
            )
          })}
        </div>
      </Radio.Group>
    </Modal>
  )
}
